import React from 'react';
import { Instagram, Youtube } from 'lucide-react';
import { useSite } from '../../context/SiteContext';

// Authentic WhatsApp icon (clean rounded chat bubble + handset, matches official app)
function WAIcon({ size = 22 }) {
  return (
    <svg viewBox="0 0 24 24" width={size} height={size} aria-hidden="true">
      <path fill="#FFFFFF" d="M.057 24l1.687-6.163a11.867 11.867 0 01-1.587-5.946C.16 5.335 5.495 0 12.05 0a11.817 11.817 0 018.413 3.488 11.824 11.824 0 013.48 8.414c-.003 6.555-5.338 11.892-11.893 11.892a11.9 11.9 0 01-5.688-1.448L.057 24zm6.597-3.807c1.676.995 3.276 1.591 5.392 1.592 5.448 0 9.886-4.434 9.889-9.885.002-5.462-4.415-9.89-9.881-9.892-5.452 0-9.887 4.434-9.889 9.884a9.86 9.86 0 001.51 5.263l-.999 3.648 3.978-.61zm11.387-5.464c-.074-.124-.272-.198-.57-.347-.297-.149-1.758-.868-2.031-.967-.272-.099-.47-.149-.669.149-.198.297-.768.967-.941 1.165-.173.198-.347.223-.644.074-.297-.149-1.255-.462-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.297-.347.446-.521.151-.172.2-.296.3-.494.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51l-.57-.01c-.198 0-.52.074-.792.372s-1.04 1.016-1.04 2.479 1.065 2.876 1.213 3.074c.149.198 2.095 3.2 5.076 4.487.709.306 1.263.489 1.694.626.712.226 1.36.194 1.872.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413z"/>
    </svg>
  );
}
function TelegramIcon({ size = 20 }) {
  return (
    <svg viewBox="0 0 24 24" width={size} height={size} aria-hidden="true">
      <path fill="#FFFFFF" d="M12 0C5.373 0 0 5.373 0 12s5.373 12 12 12 12-5.373 12-12S18.627 0 12 0zm5.894 8.221l-1.97 9.28c-.145.658-.537.818-1.084.508l-3-2.21-1.446 1.394c-.14.18-.357.295-.6.295-.002 0-.003 0-.005 0l.213-3.054 5.56-5.022c.24-.213-.054-.334-.373-.121l-6.869 4.326-2.96-.924c-.64-.203-.658-.643.135-.953l11.566-4.458c.538-.196 1.006.128.832.939z"/>
    </svg>
  );
}

const SOCIAL_META = {
  whatsapp: { label: 'Chat on WhatsApp', bg: '#25d366', size: 'w-12 h-12 md:w-14 md:h-14', icon: 26 },
  telegram: { label: 'Telegram', bg: '#229ED9', size: 'w-12 h-12 md:w-14 md:h-14', icon: 26 },
  instagram: { label: 'Instagram', bg: 'linear-gradient(45deg,#f58529,#dd2a7b,#8134af)', size: 'w-12 h-12 md:w-14 md:h-14', icon: 26 },
  youtube: { label: 'YouTube', bg: '#FF0000', size: 'w-12 h-12 md:w-14 md:h-14', icon: 26 }
};

function renderIcon(key, size) {
  if (key === 'whatsapp') return <WAIcon size={size} />;
  if (key === 'telegram') return <TelegramIcon size={size} />;
  if (key === 'instagram') return <Instagram size={size} color="#fff" />;
  if (key === 'youtube') return <Youtube size={size} color="#fff" />;
  return null;
}

export default function FloatingSocial() {
  const { data } = useSite();
  const s = data.social || {};
  const visibility = data.socialVisibility || { whatsapp: true, telegram: true, instagram: true, youtube: true };
  const items = Object.keys(SOCIAL_META).filter((k) => s[k] && visibility[k] !== false);
  if (items.length === 0) return null;

  return (
    <div className="fixed right-3 md:right-5 bottom-4 md:bottom-6 z-30 flex flex-col items-end gap-2.5" data-testid="floating-social">
      {items.map((k, idx) => {
        const meta = SOCIAL_META[k];
        const isWA = k === 'whatsapp';
        const bgStyle = meta.bg.startsWith('linear-gradient') ? { background: meta.bg } : { backgroundColor: meta.bg };
        return (
          <a
            key={k}
            href={s[k]}
            target="_blank"
            rel="noreferrer"
            style={{ ...bgStyle, animationDelay: `${idx * 80}ms` }}
            className={`group relative slide-in-left ${meta.size} rounded-full text-white shadow-lg flex items-center justify-center hover:scale-110 transition-transform`}
            aria-label={meta.label}
            data-testid={isWA ? 'floating-whatsapp' : `floating-${k}`}
          >
            {/* All social icons get a pulse ring — WhatsApp pulses brighter */}
            <span
              className="absolute inset-0 rounded-full social-pulse-ring pointer-events-none"
              style={{
                ...bgStyle,
                animationDelay: `${idx * 0.45}s`,
                opacity: isWA ? 0.6 : 0.4,
              }}
            />
            <span className="relative drop-shadow">{renderIcon(k, meta.icon)}</span>
            <span className="absolute right-full mr-2 text-white text-xs font-bold px-2.5 py-1.5 rounded-full shadow opacity-0 -translate-x-2 group-hover:opacity-100 group-hover:translate-x-0 transition pointer-events-none whitespace-nowrap" style={bgStyle}>
              {meta.label}
            </span>
          </a>
        );
      })}
    </div>
  );
}
